import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Gauge, ExternalLink, UserPlus } from 'lucide-react'
import api from '../services/api'
import CohortDashboardNavbar from '../components/CohortDashboardNavbar'
import CohortCheckInModal from '../components/CohortCheckInModal'
import BookOneOnOneModal from '../components/BookOneOnOneModal'
import Session4HealthFormModal from '../components/Session4HealthFormModal'
import Session12DailyRitualsFormModal from '../components/Session12DailyRitualsFormModal'
import { getToken, getUserData } from '../utils/userData'
import { API_ENDPOINTS, ROUTES, COHORT_DISPLAY_NAMES } from '../utils/constants'
import { formatSessionDate, formatSessionTime } from '../utils/helpers'

const Build3CohortDashboard = ({ programSlug = 'build3-cohort-7' }) => {
  const [stats, setStats] = useState(null)
  const [userSignup, setUserSignup] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [checkInModal, setCheckInModal] = useState({ isOpen: false, type: null })
  const [checkIns, setCheckIns] = useState([])
  const [bookOneOnOneModal, setBookOneOnOneModal] = useState(false)
  const [allSessions, setAllSessions] = useState([])
  const [loggedInUser, setLoggedInUser] = useState(null)
  const [healthFormModal, setHealthFormModal] = useState(false)
  const [healthFormResponse, setHealthFormResponse] = useState(null)
  const [session12FormModal, setSession12FormModal] = useState(false)
  const [session12FormResponse, setSession12FormResponse] = useState(null)
  const cohortDisplayName = COHORT_DISPLAY_NAMES[programSlug] || 'Build3 Energizer Sessions'


  // Check if all sessions are in the past
  const allSessionsArePast = allSessions.every(s => s.isPast)

  // Get current session (most recent non-past session, or null if all are past)
  const getCurrentSession = () => {
    // If all sessions are past, return null (no current session)
    if (allSessionsArePast) {
      return null
    }
    // Return the first non-past session, or the latest session
    const nonPastSession = allSessions.find(s => !s.isPast)
    if (nonPastSession) {
      return nonPastSession
    }
    // Fallback: return the latest available session
    const sessionIndex = Math.max(0, allSessions.length - 1)
    return allSessions[sessionIndex]
  }

  const currentSession = getCurrentSession()
  // If no current session (all past), all sessions are past sessions
  const pastSessions = currentSession 
    ? allSessions.filter(s => s.number < currentSession.number)
    : [...allSessions]

  const upcomingSessions = currentSession
    ? allSessions.filter(s => !s.isPast && s.number !== currentSession.number)
    : []

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch sessions from API
      const sessionsResponse = await api.get(API_ENDPOINTS.PROGRAMS.SESSIONS(programSlug))
      if (sessionsResponse.data.success) {
        setAllSessions(sessionsResponse.data.data)
      }

      // Fetch program stats using new endpoint
      const statsResponse = await api.get(API_ENDPOINTS.PROGRAMS.STATS(programSlug))
      if (statsResponse.data.success) {
        setStats(statsResponse.data.data)
      }

      // Check if user has signed up (check for token)
      const token = getToken()

      if (token) {
        // Fetch fresh data from server
        try {
          const meResponse = await api.get(API_ENDPOINTS.PROGRAMS.ENROLLMENT(programSlug))
          if (meResponse.data.success) {
            // Flatten the data for backward compatibility with component
            const { user, enrollment } = meResponse.data.data
            const flattenedData = {
              id: user.id,
              name: user.name,
              email: user.email,
              phone: user.phone,
              emailVerified: user.emailVerified,
              ...enrollment.data,
            }
            setUserSignup(flattenedData)
            fetchCheckIns()
            if (programSlug === 'energizers-march-cohort') {
              fetchHealthFormResponse()
              fetchSession12FormResponse()
            }
          }
        } catch (err) {
          if (err.response?.status !== 401) {
            console.error('Error fetching enrollment:', err)
            const cached = getUserData()
            if (cached) {
              setLoggedInUser(cached.user || cached)
            } else {
              try {
                const meRes = await api.get('/api/auth/me')
                if (meRes.data.success) setLoggedInUser(meRes.data.data)
              } catch (_) {}
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err)
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const fetchCheckIns = async () => {
    try {
      // Fetch check-ins using new endpoint (token added by interceptor)
      const response = await api.get(API_ENDPOINTS.PROGRAMS.CHECK_INS(programSlug))
      if (response.data.success) {
        setCheckIns(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching check-ins:', error)
    }
  }

  const fetchHealthFormResponse = async () => {
    try {
      const response = await api.get(`/api/programs/${programSlug}/session-forms/4`)
      if (response.data.success) {
        setHealthFormResponse(response.data.data)
      }
    } catch (_) {}
  }

  const fetchSession12FormResponse = async () => {
    try {
      const response = await api.get(`/api/programs/${programSlug}/session-forms/12`)
      if (response.data.success) {
        setSession12FormResponse(response.data.data)
      }
    } catch (_) {}
  }

  const handleCheckInSuccess = () => {
    fetchCheckIns()
  }

  const openCheckInModal = (type, sessionNumber) => {
    setCheckInModal({ isOpen: true, type, sessionNumber })
  }

  const closeCheckInModal = () => {
    setCheckInModal({ isOpen: false, type: null, sessionNumber: null })
  }

  // Check if check-ins exist for a specific session
  const hasPreEventCheckIn = (sessionNumber) => checkIns.some(
    (ci) => ci.sessionNumber === sessionNumber && ci.checkInType === 'pre-event'
  )
  const hasPostEventCheckIn = (sessionNumber) => checkIns.some(
    (ci) => ci.sessionNumber === sessionNumber && ci.checkInType === 'post-event'
  )

  // Get check-in data for a specific session and type
  const getCheckIn = (sessionNumber, type) => checkIns.find(
    (ci) => ci.sessionNumber === sessionNumber && ci.checkInType === type
  )

  // Get sessions with both pre and post check-ins completed
  const getCompletedSessions = () => {
    return allSessions.filter(session => 
      hasPreEventCheckIn(session.number) && hasPostEventCheckIn(session.number)
    )
  }

  const scoreLabels = {
    energyLevel: { label: 'Energy Level', emoji: '⚡', color: 'from-yellow-400 to-orange-500' },
    stressLevel: { label: 'Stress Level', emoji: <Gauge className="inline w-[1em] h-[1em]" />, color: 'from-red-400 to-pink-500' },
    emotionalState: { label: 'Emotional State', emoji: '💜', color: 'from-purple-400 to-indigo-500' },
    physicalState: { label: 'Physical State', emoji: '💪', color: 'from-green-400 to-teal-500' },
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full"
        />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md text-center">
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50">
      {/* Dashboard Navbar - Show when user is authenticated */}
      {userSignup && <CohortDashboardNavbar userSignup={userSignup} programSlug={programSlug} />}

      <div className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {!userSignup && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-amber-50 border border-amber-200 rounded-2xl p-4 md:p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4"
            >
              <div className="flex-1">
                <p className="font-semibold text-amber-900 text-sm md:text-base">
                  You&apos;re not enrolled in this cohort yet
                </p>
                <p className="text-amber-700 text-xs md:text-sm mt-0.5">
                  Enroll now to track your wellness journey and access personal check-ins
                </p>
              </div>
              <Link
                to={ROUTES.COHORT_SIGNUP(programSlug)}
                state={loggedInUser ? {
                  prefillEmail: loggedInUser.email,
                  prefillName: loggedInUser.name,
                  prefillPhone: loggedInUser.phone,
                } : undefined}
                className="shrink-0 px-5 py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 transition-colors"
              >
                Enroll Now
              </Link>
            </motion.div>
          )}

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {cohortDisplayName} Dashboard
            </h1>
            <p className="text-xl text-gray-600">
              Energizer Session Wellness Metrics
            </p>
          </motion.div>

          {/* Current Session Card OR Program Complete Card */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            {allSessionsArePast ? (
              /* Program Complete Card - All sessions are past */
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="pl-4 text-xl font-semibold text-gray-700">Program Complete</h3>
                  {pastSessions.length > 0 && (
                    <button
                      onClick={() => document.getElementById('previous-sessions')?.scrollIntoView({ behavior: 'smooth' })}
                      className="flex items-center text-purple-600 hover:text-purple-800 transition-colors font-medium text-base"
                    >
                      <span className="mr-1">📚</span>
                      View All Sessions ({pastSessions.length})
                      <span className="ml-1">↓</span>
                    </button>
                  )}
                </div>
                <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-3xl shadow-xl p-8 border-2 border-green-200">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div className="flex-1">
                      <div className="mb-2">
                        <span className="text-sm font-semibold text-green-700 uppercase tracking-wide">
                          🎉 Congratulations!
                        </span>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                        Build3 Energizer Sessions are done!
                      </h2>
                      <p className="text-lg text-gray-700 mb-4">
                        We&apos;ve completed {allSessions.length} sessions! 
                      </p>
                      <p className="w-2/3 text-lg text-gray-700 mb-4">
                        Continue your wellness journey by booking personalized 1-on-1 sessions with our facilitators.
                      </p>
                      {/* <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
                        <div className="flex items-center">
                          <span className="text-xl mr-2">✅</span>
                          <span className="font-semibold">{allSessions.length} Sessions Completed</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-xl mr-2">👥</span>
                          <span className="font-semibold">{stats?.totalSignups || 0} Members</span>
                        </div>
                      </div> */}
                      {/* Book 1-on-1 Session CTA - Main action for completed program */}
                      {userSignup && (
                        <button
                          onClick={() => setBookOneOnOneModal(true)}
                          className="px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg flex items-center gap-2"
                        >
                          <UserPlus className="w-5 h-5" />
                          Book 1-on-1 Session with a Facilitator
                        </button>
                      )}
                      {!userSignup && (
                        <Link
                          to={ROUTES.COHORT_SIGNUP(programSlug)}
                          className="inline-block px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg"
                        >
                          Sign Up to Access Resources
                        </Link>
                      )}
                    </div>
                    <div className="bg-white/60 rounded-2xl p-6 text-center min-w-[180px] border border-green-100">
                      <div className="text-5xl mb-2">🏆</div>
                      <div className="text-sm text-green-600 font-semibold uppercase tracking-wider mb-1">Journey Complete</div>
                      <div className="text-lg font-bold text-gray-900">Continue Growing</div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Current Session Card - When there's an active session */
              <>
                {/* Header row above card */}
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="pl-4 text-xl font-semibold text-gray-700">Next Session</h3>
                  <div className="flex items-center gap-4">
                    {upcomingSessions.length > 0 && (
                      <button
                        onClick={() => document.getElementById('upcoming-sessions')?.scrollIntoView({ behavior: 'smooth' })}
                        className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors font-medium text-base"
                      >
                        <span className="mr-1">📅</span>
                        Upcoming ({upcomingSessions.length})
                        <span className="ml-1">↓</span>
                      </button>
                    )}
                    {pastSessions.length > 0 && (
                      <button
                        onClick={() => document.getElementById('previous-sessions')?.scrollIntoView({ behavior: 'smooth' })}
                        className="flex items-center text-purple-600 hover:text-purple-800 transition-colors font-medium text-base"
                      >
                        <span className="mr-1">📚</span>
                        Past ({pastSessions.length})
                        <span className="ml-1">↓</span>
                      </button>
                    )}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-pink-50 via-purple-50 to-primary-50 rounded-3xl shadow-xl p-8 border-2 border-purple-200">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div className="flex-1">
                      <div className="mb-2">
                        <span className="text-sm font-semibold text-purple-700 uppercase tracking-wide">
                          {/* {cohortDisplayName} */}
                        </span>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                        Session {currentSession.number}: {currentSession.title}
                      </h2>
                      <p className="text-lg text-gray-700 mb-4 italic">
                        {currentSession.subtitle}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
                        <div className="flex items-center">
                          <span className="text-xl mr-2">📅</span>
                          <span className="font-semibold">{formatSessionDate(currentSession.sessionDateTime)}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-xl mr-2">⏰</span>
                          <span className="font-semibold">{formatSessionTime(currentSession.sessionDateTime, currentSession.endDateTime)}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-xl mr-2">📍</span>
                          <span className="font-semibold">{currentSession.location}</span>
                        </div>
                        {currentSession.pdf && (
                          <a
                            href={currentSession.pdf.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-purple-600 hover:text-purple-800 transition-colors"
                          >
                            <span className="text-xl mr-2">📄</span>
                            <span className="font-semibold underline">{currentSession.pdf.title || 'Session PDF'}</span>
                          </a>
                        )}
                      </div>
                      {/* Check-In Buttons - Only show if user is authenticated */}
                      {userSignup && (
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => openCheckInModal('pre-event', currentSession.number)}
                            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                              hasPreEventCheckIn(currentSession.number)
                                ? 'bg-green-100 text-green-700 border-2 border-green-300'
                                : 'bg-primary-600 text-white hover:bg-primary-700'
                            }`}
                          >
                            {hasPreEventCheckIn(currentSession.number) ? '✓ Pre-Event Check-In Complete' : 'Pre-Event Check-In'}
                          </button>
                          <button
                            onClick={() => openCheckInModal('post-event', currentSession.number)}
                            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                              hasPostEventCheckIn(currentSession.number)
                                ? 'bg-green-100 text-green-700 border-2 border-green-300'
                                : 'bg-purple-600 text-white hover:bg-purple-700'
                            }`}
                          >
                            {hasPostEventCheckIn(currentSession.number) ? '✓ Post-Event Check-In Complete' : 'Post-Event Check-In'}
                          </button>
                          {programSlug === 'energizers-march-cohort' && currentSession.number === 4 && (
                            healthFormResponse ? (
                              <div className="flex items-center gap-2">
                                <span className="flex items-center gap-1.5 text-green-700 font-semibold text-sm">
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Health Form Submitted
                                </span>
                                <button
                                  onClick={() => setHealthFormModal(true)}
                                  className="text-teal-600 hover:text-teal-700 text-sm font-semibold underline underline-offset-2 transition-colors"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setHealthFormModal(true)}
                                className="text-teal-600 hover:text-teal-700 font-semibold text-sm underline underline-offset-2 transition-colors"
                              >
                                Fill Health Form
                              </button>
                            )
                          )}
                          {programSlug === 'energizers-march-cohort' && currentSession.number === 12 && (
                            session12FormResponse ? (
                              <div className="flex items-center gap-2">
                                <span className="flex items-center gap-1.5 text-green-700 font-semibold text-sm">
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Daily rituals form submitted
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setSession12FormModal(true)}
                                  className="text-teal-600 hover:text-teal-700 text-sm font-semibold underline underline-offset-2 transition-colors"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setSession12FormModal(true)}
                                className="text-teal-600 hover:text-teal-700 font-semibold text-sm underline underline-offset-2 transition-colors"
                              >
                                Designing your daily rituals
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                    <div className="bg-white/60 rounded-2xl p-4 text-center min-w-[180px] border border-purple-100">
                      <div className="text-sm text-purple-600 font-semibold uppercase tracking-wider mb-1">Facilitator</div>
                      <div className="text-xl font-bold text-gray-900 mb-0.5">
                        {currentSession.facilitatorSlug ? (
                          <Link 
                            to={`/facilitator/${currentSession.facilitatorSlug}`}
                            className="hover:text-purple-600 transition-colors underline decoration-purple-300 inline-flex items-center gap-1"
                          >
                            {currentSession.facilitator}
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        ) : (
                          currentSession.facilitator
                        )}
                      </div>
                      <div className="text-sm text-gray-600">{currentSession.facilitatorTitle}</div>
                    </div>
                  </div>

                  {/* Current Session Score Comparison - Show when both check-ins are done */}
                  {userSignup && hasPreEventCheckIn(currentSession.number) && hasPostEventCheckIn(currentSession.number) && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="mt-6 pt-6 border-t border-purple-200"
                    >
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <span className="mr-2">📊</span>
                        Your Session Results
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(scoreLabels).map(([key, { label, emoji }]) => {
                          const preCheckIn = getCheckIn(currentSession.number, 'pre-event')
                          const postCheckIn = getCheckIn(currentSession.number, 'post-event')
                          const preValue = preCheckIn?.[key] || 0
                          const postValue = postCheckIn?.[key] || 0
                          const diff = postValue - preValue
                          // For stress, lower is better
                          const isStress = key === 'stressLevel'
                          const isImproved = isStress ? diff < 0 : diff > 0
                          const isDeclined = isStress ? diff > 0 : diff < 0

                          return (
                            <div
                              key={key}
                              className="bg-white/80 backdrop-blur rounded-xl p-4 shadow-sm"
                            >
                              <div className="flex items-center justify-center mb-2">
                                <span className="text-2xl">{emoji}</span>
                              </div>
                              <div className="text-center text-xs font-medium text-gray-600 mb-2">{label}</div>
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-lg font-semibold text-gray-500">{preValue}</span>
                                <span className="text-gray-400">→</span>
                                <span className="text-lg font-bold text-gray-800">{postValue}</span>
                              </div>
                              <div className="text-center mt-2">
                                <span
                                  className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${
                                    isImproved
                                      ? 'bg-green-100 text-green-700'
                                      : isDeclined
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {diff > 0 ? '+' : ''}{diff}
                                  {isImproved && ' ↑'}
                                  {isDeclined && ' ↓'}
                                  {diff === 0 && ' →'}
                                </span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                      {/* Summary message */}
                      {(() => {
                        const preCheckIn = getCheckIn(currentSession.number, 'pre-event')
                        const postCheckIn = getCheckIn(currentSession.number, 'post-event')
                        const improvements = Object.keys(scoreLabels).filter(key => {
                          const diff = (postCheckIn?.[key] || 0) - (preCheckIn?.[key] || 0)
                          return key === 'stressLevel' ? diff < 0 : diff > 0
                        })
                        
                        if (improvements.length >= 3) {
                          return (
                            <div className="mt-4 flex items-center text-green-700 bg-green-100/80 rounded-lg px-4 py-3">
                              <span className="text-xl mr-2">🎉</span>
                              <span className="font-medium">Amazing! You showed improvement in {improvements.length} wellness areas this session!</span>
                            </div>
                          )
                        } else if (improvements.length > 0) {
                          return (
                            <div className="mt-4 flex items-center text-purple-700 bg-purple-100/80 rounded-lg px-4 py-3">
                              <span className="text-xl mr-2">✨</span>
                              <span className="font-medium">Great progress! You improved in {improvements.length} area{improvements.length > 1 ? 's' : ''}.</span>
                            </div>
                          )
                        } else {
                          return (
                            <div className="mt-4 flex items-center text-gray-600 bg-gray-100/80 rounded-lg px-4 py-3">
                              <span className="text-xl mr-2">💭</span>
                              <span className="font-medium">Every session is a step forward. Keep up the wellness journey!</span>
                            </div>
                          )
                        }
                      })()}
                    </motion.div>
                  )}
                </div>
              </>
            )}
          </motion.div>

          {/* Check-In Modal */}
          {checkInModal.isOpen && (
            <CohortCheckInModal
              isOpen={checkInModal.isOpen}
              onClose={closeCheckInModal}
              sessionNumber={checkInModal.sessionNumber}
              checkInType={checkInModal.type}
              onSuccess={handleCheckInSuccess}
              programSlug={programSlug}
            />
          )}

          {/* Health Form Modal */}
          {programSlug === 'energizers-march-cohort' && (
            <Session4HealthFormModal
              isOpen={healthFormModal}
              onClose={() => setHealthFormModal(false)}
              programSlug={programSlug}
              existingResponse={healthFormResponse}
              onSuccess={() => fetchHealthFormResponse()}
            />
          )}
          {programSlug === 'energizers-march-cohort' && (
            <Session12DailyRitualsFormModal
              isOpen={session12FormModal}
              onClose={() => setSession12FormModal(false)}
              programSlug={programSlug}
              existingResponse={session12FormResponse}
              onSuccess={() => fetchSession12FormResponse()}
            />
          )}

          {/* Book 1-on-1 Session Modal */}
          <BookOneOnOneModal
            isOpen={bookOneOnOneModal}
            onClose={() => setBookOneOnOneModal(false)}
            facilitators={allSessions.map(s => ({
              name: s.facilitator,
              title: s.title,
              facilitatorTitle: s.facilitatorTitle,
              session: s.number,
            }))}
            onSuccess={() => {
              // Optional: show a toast or refresh data
              console.log('Booking request submitted successfully')
            }}
            programSlug={programSlug}
          />

          {/* Cohort Overview: Members + Average Scores */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-xl p-5 md:p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg md:text-xl font-semibold text-gray-700 flex items-center">
                <span className="mr-2">📊</span>
                Cohort Overview
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 hidden sm:inline">Members</span>
                <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent leading-none">
                  {stats?.totalSignups || 0}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(scoreLabels).map(([key, { label, emoji, color }]) => {
                const avgValue = stats?.averages?.[key] || 0
                const percentage = (avgValue / 10) * 100

                return (
                  <div key={key} className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-2 h-5">
                      <span className="text-lg leading-none">{emoji}</span>
                      <span className="text-xs font-medium text-gray-600 truncate leading-none">{label}</span>
                    </div>
                    <div className="flex items-end justify-between mb-1.5">
                      <span className="text-xl font-bold text-gray-900">{avgValue.toFixed(1)}</span>
                      <span className="text-[10px] text-gray-400">/10</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full bg-gradient-to-r ${color} rounded-full`}
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </motion.div>

        {/* User's Own Scores (if signed up) */}
        {userSignup && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-r from-primary-600 to-purple-600 rounded-3xl shadow-xl p-5 md:p-6 mb-8 text-white"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg md:text-xl font-semibold flex items-center">
                <span className="mr-2">🌟</span>
                Your Initial Wellness Scores
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(scoreLabels).map(([key, { label, emoji }]) => (
                <div key={key} className="bg-white/10 backdrop-blur rounded-xl p-3 text-center flex flex-col">
                  <div className="flex items-center justify-center gap-1.5 mb-1 h-5">
                    <span className="text-lg leading-none">{emoji}</span>
                    <span className="text-xs opacity-80 leading-none">{label}</span>
                  </div>
                  <div className="text-2xl font-bold mt-auto">{userSignup[key]}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-white/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <div>
                  <span className="opacity-70">Experience:</span>{' '}
                  <span className="font-medium">{userSignup.yogaExperience}</span>
                </div>
                <div>
                  <span className="opacity-70">Goal:</span>{' '}
                  <span className="font-medium capitalize">
                    {userSignup.wellnessGoal?.replace(/-/g, ' ')}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setBookOneOnOneModal(true)}
                className="shrink-0 px-4 py-2 bg-white text-purple-600 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-1.5 shadow-lg"
              >
                <UserPlus className="w-4 h-4" />
                Book 1-on-1 Session
              </button>
            </div>
          </motion.div>
        )}

          {/* Upcoming Sessions */}
          {upcomingSessions.length > 0 && (
            <motion.div
              id="upcoming-sessions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mb-8 scroll-mt-8"
            >
              <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                <span className="mr-2">📅</span>
                Upcoming Sessions
              </h3>
              <div className="space-y-4">
                {upcomingSessions.map((session) => (
                  <div
                    key={session.number}
                    className="bg-white rounded-2xl shadow-lg p-6 border border-indigo-100"
                  >
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="text-xs font-semibold text-indigo-500 uppercase tracking-wide">
                            Session {session.number}
                          </span>
                          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-full border border-indigo-200">
                            UPCOMING
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {session.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3 italic">
                          {session.subtitle}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-3">
                          <div className="flex items-center">
                            <span className="mr-1">📅</span>
                            <span>{formatSessionDate(session.sessionDateTime)}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="mr-1">⏰</span>
                            <span>{formatSessionTime(session.sessionDateTime, session.endDateTime)}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="mr-1">📍</span>
                            <span>{session.location}</span>
                          </div>
                          {session.pdf && (
                            <a
                              href={session.pdf.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-purple-600 hover:text-purple-800 transition-colors"
                            >
                              <span className="mr-1">📄</span>
                              <span className="underline">{session.pdf.title || 'PDF'}</span>
                            </a>
                          )}
                        </div>
                        <div className="inline-flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                          <span className="text-lg">👤</span>
                          <div>
                            {session.facilitatorSlug ? (
                              <Link
                                to={`/facilitator/${session.facilitatorSlug}`}
                                className="text-purple-700 font-semibold hover:text-purple-900 transition-colors underline decoration-purple-300 inline-flex items-center gap-1"
                              >
                                {session.facilitator}
                                <ExternalLink className="w-3 h-3" />
                              </Link>
                            ) : (
                              <span className="text-purple-700 font-semibold">{session.facilitator}</span>
                            )}
                            <span className="text-xs text-purple-500 block">{session.facilitatorTitle}</span>
                          </div>
                        </div>
                      </div>
                      {userSignup && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openCheckInModal('pre-event', session.number)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                              hasPreEventCheckIn(session.number)
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                            }`}
                          >
                            {hasPreEventCheckIn(session.number) ? '✓ Pre' : 'Pre Check-In'}
                          </button>
                          <button
                            onClick={() => openCheckInModal('post-event', session.number)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                              hasPostEventCheckIn(session.number)
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                            }`}
                          >
                            {hasPostEventCheckIn(session.number) ? '✓ Post' : 'Post Check-In'}
                          </button>
                          {programSlug === 'energizers-march-cohort' && session.number === 4 && (
                            healthFormResponse ? (
                              <div className="flex items-center gap-1.5">
                                <span className="flex items-center gap-1 text-green-700 font-semibold text-xs">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Health Form
                                </span>
                                <button
                                  onClick={() => setHealthFormModal(true)}
                                  className="text-teal-600 hover:text-teal-700 text-xs font-semibold underline underline-offset-2 transition-colors"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setHealthFormModal(true)}
                                className="text-teal-600 hover:text-teal-700 text-xs font-semibold underline underline-offset-2 transition-colors"
                              >
                                Health Form
                              </button>
                            )
                          )}
                          {programSlug === 'energizers-march-cohort' && session.number === 12 && (
                            session12FormResponse ? (
                              <div className="flex items-center gap-1.5">
                                <span className="flex items-center gap-1 text-green-700 font-semibold text-xs">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Rituals form
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setSession12FormModal(true)}
                                  className="text-teal-600 hover:text-teal-700 text-xs font-semibold underline underline-offset-2 transition-colors"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setSession12FormModal(true)}
                                className="text-teal-600 hover:text-teal-700 text-xs font-semibold underline underline-offset-2 transition-colors"
                              >
                                Daily rituals
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

        {/* Session Score Comparisons - When both pre and post check-ins are done */}
        {userSignup && getCompletedSessions().length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-3xl shadow-xl p-8 mb-8"
          >
            <h2 className="text-2xl font-semibold text-gray-700 mb-6 flex items-center">
              <span className="mr-3">📈</span>
              Your Session Progress
            </h2>
            <div className="space-y-8">
              {getCompletedSessions().map((session) => {
                const preCheckIn = getCheckIn(session.number, 'pre-event')
                const postCheckIn = getCheckIn(session.number, 'post-event')

                return (
                  <div key={session.number} className="bg-gray-50 rounded-2xl p-6">
                    <div className="mb-4 pb-4 border-b border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900">
                        Session {session.number}: {session.title}
                      </h3>
                      <p className="text-sm text-gray-500">{formatSessionDate(session.sessionDateTime)}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(scoreLabels).map(([key, { label, emoji, color }]) => {
                        const preValue = preCheckIn?.[key] || 0
                        const postValue = postCheckIn?.[key] || 0
                        const diff = postValue - preValue
                        // For stress, lower is better, so we invert the "good" indicator
                        const isStress = key === 'stressLevel'
                        const isImproved = isStress ? diff < 0 : diff > 0
                        const isDeclined = isStress ? diff > 0 : diff < 0

                        return (
                          <motion.div
                            key={key}
                            className="bg-white rounded-xl p-4 shadow-sm"
                            whileHover={{ scale: 1.02 }}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center">
                                <span className="text-2xl mr-2">{emoji}</span>
                                <span className="font-medium text-gray-700 text-sm">{label}</span>
                              </div>
                              <div
                                className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  isImproved
                                    ? 'bg-green-100 text-green-700'
                                    : isDeclined
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {diff > 0 ? '+' : ''}{diff}
                                {isImproved && ' ↑'}
                                {isDeclined && ' ↓'}
                                {diff === 0 && ' →'}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <div className="text-xs text-gray-500 mb-1">Pre-Event</div>
                                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                                  <motion.div
                                    className="h-full bg-gray-400 rounded-full"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${(preValue / 10) * 100}%` }}
                                    transition={{ duration: 0.5, ease: 'easeOut' }}
                                  />
                                </div>
                                <div className="text-center text-sm font-semibold text-gray-600 mt-1">{preValue}</div>
                              </div>
                              <div className="text-gray-400">→</div>
                              <div className="flex-1">
                                <div className="text-xs text-gray-500 mb-1">Post-Event</div>
                                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                                  <motion.div
                                    className={`h-full bg-gradient-to-r ${color} rounded-full`}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${(postValue / 10) * 100}%` }}
                                    transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
                                  />
                                </div>
                                <div className="text-center text-sm font-semibold text-gray-800 mt-1">{postValue}</div>
                              </div>
                            </div>
                          </motion.div>
                        )
                      })}
                    </div>
                    {/* Summary for this session */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      {(() => {
                        const preCheckInData = getCheckIn(session.number, 'pre-event')
                        const postCheckInData = getCheckIn(session.number, 'post-event')
                        const improvements = Object.keys(scoreLabels).filter(key => {
                          const diff = (postCheckInData?.[key] || 0) - (preCheckInData?.[key] || 0)
                          return key === 'stressLevel' ? diff < 0 : diff > 0
                        })
                        
                        if (improvements.length >= 3) {
                          return (
                            <div className="flex items-center text-green-700 bg-green-50 rounded-lg px-4 py-2">
                              <span className="text-xl mr-2">🎉</span>
                              <span className="font-medium">Great session! You showed improvement in {improvements.length} areas.</span>
                            </div>
                          )
                        } else if (improvements.length > 0) {
                          return (
                            <div className="flex items-center text-primary-700 bg-primary-50 rounded-lg px-4 py-2">
                              <span className="text-xl mr-2">✨</span>
                              <span className="font-medium">Good progress! You improved in {improvements.length} area{improvements.length > 1 ? 's' : ''}.</span>
                            </div>
                          )
                        } else {
                          return (
                            <div className="flex items-center text-gray-600 bg-gray-100 rounded-lg px-4 py-2">
                              <span className="text-xl mr-2">💭</span>
                              <span className="font-medium">Keep going! Wellness is a journey, not a destination.</span>
                            </div>
                          )
                        }
                      })()}
                    </div>
                  </div>
                )
              })}
            </div>
          </motion.div>
        )}

        {/* Comparison Section (if user signed up) */}
        {/* {userSignup && stats?.averages && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-3xl shadow-xl p-8 mb-8"
          >
            <h2 className="text-2xl font-semibold text-gray-700 mb-6 flex items-center">
              <span className="mr-3">📈</span>
              Your Scores vs Cohort Average
            </h2>
            <div className="space-y-6">
              {Object.entries(scoreLabels).map(([key, { label, emoji }]) => {
                const userValue = userSignup[key]
                const avgValue = stats.averages[key]
                const diff = userValue - avgValue
                const isAbove = diff > 0

                return (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{emoji}</span>
                      <span className="font-medium text-gray-700">{label}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <span className="text-sm text-gray-500">You: </span>
                        <span className="font-bold text-gray-900">{userValue}</span>
                      </div>
                      <div className="w-16 text-center">
                        <span
                          className={`text-sm font-medium ${
                            isAbove ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-500'
                          }`}
                        >
                          {diff > 0 ? '+' : ''}
                          {diff.toFixed(1)}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm text-gray-500">Avg: </span>
                        <span className="font-bold text-gray-900">{avgValue.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </motion.div>
        )} */}

          {/* Past Sessions */}
          {pastSessions.length > 0 && (
            <motion.div
              id="previous-sessions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="mb-8 scroll-mt-8"
            >
              <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                <span className="mr-2">📚</span>
                {allSessionsArePast ? 'All Sessions' : 'Previous Sessions'}
              </h3>
              <div className="space-y-4">
                {pastSessions.reverse().map((session) => (
                  <div
                    key={session.number}
                    className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200"
                  >
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Session {session.number}
                          </span>
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                            COMPLETED
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {session.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3 italic">
                          {session.subtitle}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-3">
                          <div className="flex items-center">
                            <span className="mr-1">📅</span>
                            <span>{formatSessionDate(session.sessionDateTime)}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="mr-1">⏰</span>
                            <span>{formatSessionTime(session.sessionDateTime, session.endDateTime)}</span>
                          </div>
                          {session.pdf && (
                            <a
                              href={session.pdf.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-purple-600 hover:text-purple-800 transition-colors"
                            >
                              <span className="mr-1">📄</span>
                              <span className="underline">{session.pdf.title || 'PDF'}</span>
                            </a>
                          )}
                        </div>
                        {/* Facilitator highlight */}
                        <div className="inline-flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                          <span className="text-lg">👤</span>
                          <div>
                            {session.facilitatorSlug ? (
                              <Link 
                                to={`/facilitator/${session.facilitatorSlug}`}
                                className="text-purple-700 font-semibold hover:text-purple-900 transition-colors underline decoration-purple-300 inline-flex items-center gap-1"
                              >
                                {session.facilitator}
                                <ExternalLink className="w-3 h-3" />
                              </Link>
                            ) : (
                              <span className="text-purple-700 font-semibold">{session.facilitator}</span>
                            )}
                            <span className="text-xs text-purple-500 block">{session.facilitatorTitle}</span>
                          </div>
                        </div>
                      </div>
                      {/* Check-In Buttons for past sessions */}
                      {userSignup && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openCheckInModal('pre-event', session.number)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                              hasPreEventCheckIn(session.number)
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                            }`}
                          >
                            {hasPreEventCheckIn(session.number) ? '✓ Pre' : 'Pre Check-In'}
                          </button>
                          <button
                            onClick={() => openCheckInModal('post-event', session.number)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                              hasPostEventCheckIn(session.number)
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                            }`}
                          >
                            {hasPostEventCheckIn(session.number) ? '✓ Post' : 'Post Check-In'}
                          </button>
                          {programSlug === 'energizers-march-cohort' && session.number === 4 && (
                            healthFormResponse ? (
                              <div className="flex items-center gap-1.5">
                                <span className="flex items-center gap-1 text-green-700 font-semibold text-xs">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Health Form
                                </span>
                                <button
                                  onClick={() => setHealthFormModal(true)}
                                  className="text-teal-600 hover:text-teal-700 text-xs font-semibold underline underline-offset-2 transition-colors"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setHealthFormModal(true)}
                                className="text-teal-600 hover:text-teal-700 text-xs font-semibold underline underline-offset-2 transition-colors"
                              >
                                Health Form
                              </button>
                            )
                          )}
                          {programSlug === 'energizers-march-cohort' && session.number === 12 && (
                            session12FormResponse ? (
                              <div className="flex items-center gap-1.5">
                                <span className="flex items-center gap-1 text-green-700 font-semibold text-xs">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Rituals form
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setSession12FormModal(true)}
                                  className="text-teal-600 hover:text-teal-700 text-xs font-semibold underline underline-offset-2 transition-colors"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setSession12FormModal(true)}
                                className="text-teal-600 hover:text-teal-700 text-xs font-semibold underline underline-offset-2 transition-colors"
                              >
                                Daily rituals
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-8 text-gray-500"
          >
            <p>{cohortDisplayName}</p>
            <p className="text-sm mt-2">Powered by Wellness Loop</p>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Build3CohortDashboard

